<template>
    <div class="component component__system_uploader_scorm">
        <div v-if="label" class="component__system_uploader_scorm_title"
        >{{label | toCapitalize}}<span class="asterisk" v-if="required">*</span></div>
        <component-button
            :align="'left'"
            :disabled="disabled"
            :class="'component__system_uploader_scorm_buttonupload'"
            :name="`${name}-button-upload`"
            :icon="true"
            :icon-align="'right'"
            :icon-name="'upload'"
            :theme="'default'"
            :hint="'Upload a valid SCORM file to continue with the process.\n'+'The time it takes for this process may vary depending on the size of the .zip file.'"
            @click="openFileUploader"
        >{{ file || valueData ? 'Change file' : 'Upload file' }}</component-button>
        <div class="component__system_uploader_scorm_inputvalidate">
            <input
                class="is-hidden"
                type="text"
                :name="`${name}-inputvalidate`"
                :id="`${name}-inputvalidate`"
                :value="file"
                :v-validate="vValidate"
            >
            <span
                class="form__error component__system_uploader_scorm_error"
                v-if="errors.first(`${name}-inputvalidate`)"
            >{{ errors.first(`${name}-inputvalidate`) }}</span>
        </div>
        <template v-if="$slots.default">
            <div class="component__system_uploader_scorm_hint">
                <template v-if="hintButton">
                    <component-button
                        :name="'component-system-uploader-scorm-hint-button'"
                        :text="true"
                        :size="'tiny'"
                        @click="emitHint"
                    >
                        <slot></slot>
                    </component-button>
                </template>
                <template v-else>
                    <slot></slot>
                </template>
            </div>
        </template>
        <template v-if="file || valueData.name">
            <div class="component__system_uploader_scorm_filename">{{ file.name || valueData.name }}</div>
        </template>
        <template v-if="file">
            <div class="component__system_uploader_scorm_progress">
                <component-progress-bar
                    :title="'Uploading'"
                    :stage="uploadingStage"
                    :loaded="uploadProgress.loaded"
                    :total="uploadProgress.total"
                ></component-progress-bar>
                <component-progress-bar
                    :title="'Unzipping'"
                    :stage="unzippingStage"
                    :progress="unzippingStage === 'complete' ? 100 : (unzippingStage === 'in-process' ? 10 : 0)"
                ></component-progress-bar>
                <component-progress-bar
                    :title="'Validating'"
                    :stage="validatingStage"
                    :progress="validatingStage === 'complete' ? 100 : (validatingStage === 'in-process' ? 10 : 0)"
                ></component-progress-bar>
            </div>
        </template>
        <template v-if="file || valueData.id">
            <component-button
                :disabled="valueData.status !== 'ready'"
                :align="'left'"
                :class="'component__system_uploader_scorm_buttonpreview'"
                :name="`${name}-button-preview`"
                :theme="'default'"
                :hint="'The SCORM content will open in a new window.\n'+'Progress and interactions will not be saved.'"
                @click="previewSCORMModule"
            >Preview</component-button>
        </template>
        <input
            ref="uploader"
            class="component__system_uploader_scorm_input is-hidden"
            type="file"
            accept=".zip"
            :name="`${name}-inputfile`"
            :id="`system-uploader-scorm-input-${name}`"
            @change="uploadFile"
        >
    </div>
</template>

<script>
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentProgressBar from '@/components/default/shared/ComponentProgressBar'

export default {
  name: 'component-system-uploader-scorm',
  components: { ComponentProgressBar, ComponentButton },
  data () {
    return {
      file: '',
      checkFileInterval: '',
      refreshTokenInterval: '',
      isProcessingFile: false,
      uploadingStage: 'not-started',
      unzippingStage: 'not-started',
      validatingStage: 'not-started'
    }
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: 'component-system-uploader-scorm'
    },
    label: {
      type: String,
      default: 'Content'
    },
    required: {
      type: Boolean,
      default: true
    },
    hintButton: {
      type: Boolean,
      default: false
    },
    uploadDispatchName: {
      type: String,
      default: 'lmsSuperadminUsersBulkSetUsers'
    },
    setFileDispatchName: {
      type: String,
      default: 'lmsSuperadminUsersBulkSetFile'
    },
    dataVvAs: {
      type: String,
      default: 'file'
    },
    vValidate: {
      type: [String, Array, Object],
      default: 'required'
    },
    valueData: {
      type: [Object, String],
      default: () => {}
    }
  },
  computed: {
    uploadProgress () {
      return this.$store.getters.getEbsModuleCustomUploadProgress
    },
    moduleSCORMBucketUrl () {
      return this.valueData.scormBucketUrl
    },
    moduleId () {
      return this.valueData.id
    }
  },
  methods: {
    refreshTokens () {
      this.$store.dispatch('authUserRefreshFirstStageToken').then(() => {
        this.$store.dispatch('authUserRefreshSecondStageToken')
      })
    },
    resetUploading () {
      this.uploadingStage = 'not-started'
    },
    startUploading () {
      this.uploadingStage = 'in-process'
      this.isProcessingFile = true
    },
    stopUploading () {
      this.uploadingStage = 'not-completed'
    },
    completeUploading () {
      this.uploadingStage = 'complete'
    },
    resetUnzipping () {
      this.unzippingStage = 'not-started'
    },
    startUnzipping () {
      this.unzippingStage = 'in-process'
    },
    stopUnzipping () {
      this.unzippingStage = 'not-completed'
    },
    completeUnzipping () {
      this.unzippingStage = 'complete'
    },
    resetValidating () {
      this.validatingStage = 'not-started'
    },
    startValidating () {
      this.validatingStage = 'in-process'
    },
    stopValidating () {
      this.validatingStage = 'not-completed'
    },
    completeValidating () {
      this.validatingStage = 'complete'
    },
    stopProcessingFile () {
      this.isProcessingFile = false
    },
    openFileUploader () {
      this.$refs.uploader.click()
    },
    uploadFile () {
      const file = this.$refs.uploader.files[0]
      if (!file) return

      this.file = file
      this.$store.dispatch('ebsModuleCancelRequests')
      this.$store.dispatch('ebsModuleClearCustomContentData')
      this.$store.dispatch('ebsModuleClearDataContent')
      this.resetUploading()
      this.resetUnzipping()
      this.resetValidating()

      clearInterval(this.checkFileInterval)
      this.$eventBus.$emit('content-change')
      this.$store.dispatch('ebsModuleGeneratePresignedPost', { name: file.name, size: file.size }).then(response => {
        const formInputs = response.data.formInputs
        const formAttributes = response.data.formAttributes
        const key = JSON.parse(JSON.stringify(formInputs['key']))
        const fileId = key.slice(key.indexOf('/') + 1, key.indexOf('.'))
        let form = new FormData()

        form.append('acl', formInputs['acl'])
        form.append('key', formInputs['key'])
        form.append('X-Amz-Credential', formInputs['X-Amz-Credential'])
        form.append('X-Amz-Algorithm', formInputs['X-Amz-Algorithm'])
        form.append('X-Amz-Date', formInputs['X-Amz-Date'])
        form.append('Policy', formInputs['Policy'])
        form.append('X-Amz-Signature', formInputs['X-Amz-Signature'])
        form.append('file', file, file.name)

        this.startUploading()
        this.refreshTokenInterval = setInterval(this.refreshTokens, 1000 * 60 * 15)
        this.$store.dispatch('ebsModuleUploadSCORMFile', {
          action: formAttributes.action,
          enctype: formAttributes.enctype,
          method: formAttributes.method,
          form
        }).then(() => {
          this.completeUploading()
          this.startUnzipping()
          clearInterval(this.refreshTokenInterval)
          this.checkFileInterval = setInterval(this.checkFile, 2000, fileId)
        }).catch(() => {
          this.stopUploading()
        })
      })
    },
    getFile (fileId) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('ebsModuleGetSCORMFile', fileId).then(response => {
          resolve(response)
        }).catch(error => {
          reject(error)
        })
      })
    },
    checkFile (fileId) {
      this.getFile(fileId).then(response => {
        const status = response.data.status
        if (status !== 'uploaded' || status !== 'not_uploaded') {
          this.completeUnzipping()
          this.startValidating()
        }
        if (status === 'validation_failed' || status === 'ready') {
          clearInterval(this.checkFileInterval)
          this.stopProcessingFile()
        }
        if (status === 'validation_failed') {
          this.stopValidating()
          this.$store.dispatch('defaultNotificationToastsAdd', {
            title: 'SCORM upload',
            message: `SCORM file not validated properly. Try again, select a different file or contact an administrator.`,
            status: 'error'
          })
        }
        if (status === 'ready') {
          this.completeValidating()
          this.setFile(fileId)
          this.$store.dispatch('defaultNotificationToastsAdd', {
            title: 'SCORM upload',
            message: `SCORM file uploaded successfully.`,
            status: 'success'
          })
        }
      }).catch(() => {
        clearInterval(this.checkFileInterval)
        this.stopProcessingFile()
      })
    },
    setFile (fileId) {
      this.$store.dispatch('ebsModuleSetContent', fileId)
    },
    emitHint: function () {
      this.$emit('emit-hint')
    },
    previewSCORMModule () {
      const redirectWindow = window.open(`${this.moduleSCORMBucketUrl}/preview.html?id=${this.moduleId}`, '_blank')
      redirectWindow.location
    }
  },
  beforeDestroy () {
    clearInterval(this.checkFileInterval)
    clearInterval(this.refreshTokenInterval)
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/shared/componentsystemuploaderscorm";
</style>
